import go from '@/gojs/go'

import results from '@/sui/data/results'
import spread from '@/sui/tools/spread'
import math from '@/sui/tools/math'
import ice from '@/sui/tools/ice'

function excludeValue (den, inf, inc) {
  const [info, ok] = go.denom.info(den, inf)
  return !ok || inc > info.Limit
}

function updatePop (et, values, inc) {
  let r = values[et]
  if (r == null) {
    r = {}
    values[et] = r
  }

  const k = `${inc.inf}.${inc.disc}.${inc.spe}.${inc.den}.${inc.ent}`
  const value = r[k]

  if (value == null) {
    const nv = {
      inf: inc.inf,
      disc: inc.disc,
      spe: inc.spe,
      den: inc.den,
      ent: inc.ent,
      num: inc.num,
      div: inc.div,
      pop: [inc.inc]
    }
    r[k] = nv
  } else {
    value.num += inc.num
    value.div += inc.div
    value.pop.push(inc.inc)
  }
}

function extractValues (step, p) {
  const min = p.iteration * step
  const max = min + step > p.list.length ? p.list.length : min + step
  for (let j = min; j !== max; ++j) {
    const k = p.list[j]
    const i = p.incidences[k]
    const type = results.getEstablishementType(i.estb.type)
    if (type == null) {
      p.excluded.push({
        id: k,
        name: i.estb.name,
        finess: i.estb.finess,
        error: 'Establishment type not set'
      })
      continue
    }

    const mat = results.getMaternityType(i.estb.mat)

    i.incidences.forEach(
      inc => {
        if (inc.mod !== '__all__' ||
            (inc.uf !== '__all__' && inc.uf !== '__spe__')) {
          return
        }
        if (excludeValue(inc.den, inc.inf, inc.inc)) {
          inc.qua = 3
          return
        }

        if (!go.denom.isSubPopDenominator(inc.den)) {
          updatePop(type, p.values, inc)
        }

        if (mat != null && go.units.isDisciplineLinkedToMaterType(inc.spe)) {
          updatePop(mat, p.values, inc)
        }

        updatePop('TOUS', p.values, inc)
      }
    )
  }

  return max === p.list.length
}

function extractMeanAndPopstat (step, p) {
  const min = p.iteration * step
  const max = min + step > p.list.length ? p.list.length : min + step
  for (let j = min; j !== max; ++j) {
    const t = p.list[j]
    const r = p.values[t]
    if (p.means[t] == null) {
      p.means[t] = []
    }
    for (let k in r) {
      const l = r[k]
      l.s = math.statistics(l.pop)
      l.t = t
      p.popstat.push(l)
      p.means[t].push(
        {
          mod: '__all__',
          inf: l.inf,
          disc: l.disc,
          spe: l.spe,
          den: l.den,
          uf: '__all__',
          ent: l.ent,
          inc: l.s.median,
          mean: l.s.mean,
          qua: 4,
          n: l.s.n
        }
      )
    }
  }
  return max === p.list.length
}

function buildResult (step, p) {
  if (p.iteration === 0) {
    p.result.excluded = p.excluded
    p.result.popstat = results.flattenStatistics(p.popstat, false)
    p.result.incidences = {}
    p.result.getStatistics = (r) => {
      const key = results.getStatisticsKey(r)
      return p.result.popstat[key]
    }
    return false
  }

  const min = (p.iteration - 1) * step
  const max = min + step > p.list.length ? p.list.length : min + step
  for (let i = min; i !== max; ++i) {
    const t = p.list[i]
    const m = p.means[t]
    p.result.incidences[t] = results.flattenIncidences(!p.isNational, m, t)
  }
  return max === p.list.length
}

function setMeanAndQuality (incidence, mean, quality) {
  incidence.mean = mean

  const hasQuality = (
    incidence.uf === go.units.allUfName ||
      go.units.hasSpecialityDenominators(incidence.spe)
  )
  incidence.qua = hasQuality ? quality : 4
}

function setQualities (step, p) {
  const min = p.iteration * step
  const max = min + step > p.list.length ? p.list.length : min + step
  for (let j = min; j !== max; ++j) {
    const k = p.list[j]
    const i = p.incidences[k]
    const type = results.getEstablishementType(i.estb.type)
    if (type == null) {
      continue
    }

    i.incidences.forEach(
      inc => {
        if (inc.qua !== 3) {
          const stat = p.result.getStatistics(inc)
          if (stat != null) {
            const popstat = stat[type]
            if (popstat != null && popstat.pop.length > 1) {
              inc.mean = popstat.s.mean
              const outlier = inc.inc < popstat.s.inf || inc.inc > popstat.s.sup
              setMeanAndQuality(inc, popstat.s.mean, outlier ? 2 : 0)
              return
            }
          }
        }

        setMeanAndQuality(inc, inc.inc, inc.qua === 3 ? 3 : 1)
      }
    )
  }
  return max === p.list.length
}

function setMeansQualities (step, p) {
  const min = p.iteration * step
  const max = min + step > p.list.length ? p.list.length : min + step
  for (let i = min; i !== max; ++i) {
    const t = p.list[i]
    const m = p.result.incidences[t].values
    for (let k in m) {
      const e = m[k]
      const kp = results.incidenceKeyToStatisticsKey(k)
      const ps = p.result.popstat[kp]
      const popstat = ps[t]
      e.quality = popstat.s.quality
    }
  }
  return max === p.list.length
}

export default function (incidences, isNational) {
  if (incidences == null) {
    return null
  }

  const p = {
    incidences: incidences,
    excluded: [],
    values: {},
    means: {},
    popstat: [],
    result: {},
    iteration: 0,
    list: Object.keys(incidences),
    isNational: isNational
  }

  let part = 0
  const parts = [
    { f: extractValues, s: 2, n: () => p.values },
    { f: extractMeanAndPopstat, s: 1, n: () => p.means },
    { f: buildResult, s: 1, n: () => p.incidences },
    { f: setQualities, s: 1, n: () => p.result.incidences },
    { f: setMeansQualities, s: 1 }
  ]

  return spread(
    () => {
      const cp = parts[part]
      if (part < parts.length - 1) {
        if (cp.f(cp.s, p)) {
          ++part
          p.list = Object.keys(cp.n())
          p.iteration = 0
        } else {
          ++p.iteration
        }
        return { end: false }
      }

      const end = cp.f(cp.s, p)
      ++p.iteration
      return { end: end, final: ice.freeze(p.result) }
    },
    15
  )
}
